/**
 * Decodes a string containing HTML entities into a string with the corresponding characters.
 *
 * @param html - The string containing HTML entities to decode.
 * @returns The decoded string with HTML entities converted to their corresponding characters.
 */
export const decodeHtml = (html: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

/// Truthy / Falsy Bools

/**
 * A constant array of values that are considered truthy.
 * This includes the string 'true', the string '1', the number 1, and the boolean true.
 *
 * @constant
 * @type {readonly (string | number | boolean)[]}
 */
export const TruthyTrueValues = ['true', '1', 1, true] as const;
/**
 * Represents a type that includes all values from the `TruthyTrueValues` array
 * and the number `1`. This type is used to define values that are considered
 * truthy in the context of the application.
 */
export type TruthyTrue = (typeof TruthyTrueValues)[number] | 1;
/**
 * An array of values that are considered falsy in a truthy context.
 * These values include:
 * - The string 'false'
 * - The string '0'
 * - An empty string ''
 * - The number 0
 * - The boolean false
 */
export const TruthyFalseValues = ['false', '0', '', 0, false];
/**
 * A type representing a value that can be either truthy or false.
 * This type is derived from the `TruthyFalseValues` array, which contains
 * the possible values that this type can take.
 */
export type TruthyFalse = (typeof TruthyFalseValues)[number];
/**
 * Used to support passing boolean values in HTML or React format
 */
export type BoolOrStringType = TruthyFalse | TruthyTrue;
/**
 * Determines whether or not input is truthy
 * @param input value to check
 * @returns true if input is truty
 */
export const isTruthy = (input?: BoolOrStringType): boolean =>
  TruthyTrueValues.indexOf(input as TruthyTrue) !== -1;

/**
 *
 * @param string
 * @returns
 */
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
/**
 * Safely converts an unknown object to a string.
 *
 * @param obj - The object to be converted to a string.
 * @returns The string representation of the object, or an empty string if the object is null or undefined.
 */
export const safeString = (obj: unknown): string => String(obj ?? '').trim();

/**
 * Converts a snake_case string to camelCase.
 *
 * @param snakeCaseString - The string in snake_case format.
 * @returns The string converted to camelCase format.
 */
export const snakeToCamel = (snakeCaseString: string) =>
  snakeCaseString.replace(/(_\w)/g, (match) => match[1].toUpperCase());

/**
 * Converts a camelCase string to snake_case.
 *
 * @param camelCaseString - The camelCase string to be converted.
 * @returns The converted snake_case string.
 */
export const camelToSnake = (camelCaseString: string) =>
  camelCaseString.replace(/([A-Z])/g, '_$1').toLowerCase();
